// src/components/validationNotes/GardeValidationNotes.jsx
// ============================================================================
// Garde des pages de validation des notes des enseignants :
//   - la page d'une feuille (/administration/notes/saisies/:feuilleId), d'un
//     niveau ou de l'autre ;
//   - la page du mois d'une classe du primaire
//     (/administration/notes/saisies/primaire/:classeId/:moisId), `niveau="primaire"`.
// ============================================================================
//
// Ces pages ont deux entrées — le carnet d'évaluations et la page d'un
// enseignant dans le suivi — et une troisième, la cloche : la notification
// y mène directement. `ProtectedRoute` ne convient donc pas tel quel :
//   - il exige UNE porte de module (CARNET_EVALUATION ou SUIVI_ENSEIGNANTS),
//     par le groupe ; une personne venue par le suivi, ou prévenue par la
//     cloche, n'a pas forcément celle du carnet. Or ce sont les clés fines
//     du module NOTES, par le rôle, qui ouvrent ces données côté serveur — et
//     elles seules, une famille par niveau (droitsValidationNotes) :
//     NOTES.VALIDATION.* pour le collège et le lycée,
//     NOTES.VALIDATION_PRIMAIRE.* pour le primaire, sans repli de l'une sur
//     l'autre ;
//   - il ouvre la page dès qu'UNE des licences demandées est active ; il faut
//     ici les deux (l'espace enseignant ET le carnet).
//
// La page d'une feuille ne connaît son niveau qu'une fois lue : elle s'ouvre à
// qui lit l'un des deux niveaux, et le serveur refuse (403) une feuille d'un
// niveau qu'on ne lit pas. La page du mois, elle, est du primaire par
// construction : seules les clés du primaire l'ouvrent.
//
// Même ordre que ProtectedRoute pour le message : la licence d'abord (ce n'est
// pas l'administrateur des droits qu'il faut aller voir), le droit ensuite.
// ⚠️ Cet écran évite une page cassée ; c'est le serveur qui protège
// (requireFeature, clés NOTES.VALIDATION.* et NOTES.VALIDATION_PRIMAIRE.*,
// école du jeton).
// ============================================================================

import React from 'react';
import FeatureGate from '../FeatureGate';
import { useFeatures } from '../../contexts/FeaturesContext';
import {
  CLES_VALIDATION, CLES_VALIDATION_PRIMAIRE, LICENCES_VALIDATION, useAccesValidationNotes
} from './droitsValidationNotes';

const GardeValidationNotes = ({ niveau = null, children }) => {
  const { estActive } = useFeatures();
  const { chargement, lecture, lecturePrimaire, lectureSecondaire } = useAccesValidationNotes();

  if (chargement) {
    return (
      <div role="status" className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#246990]" />
          <p className="mt-4 text-gray-600">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  // La première licence fermée est celle que l'écran nomme.
  const fermee = LICENCES_VALIDATION.find((cle) => !estActive(cle));
  if (fermee) return <FeatureGate cle={fermee}>{children}</FeatureGate>;

  const permis = niveau === 'primaire' ? lecturePrimaire : (niveau === 'secondaire' ? lectureSecondaire : lecture);
  if (!permis) {
    const cles = niveau === 'primaire'
      ? `la permission de lecture des saisies du primaire, dans le module Notes (${CLES_VALIDATION_PRIMAIRE.VOIR})`
      : (niveau === 'secondaire'
        ? `la permission de lecture des saisies du collège et du lycée, dans le module Notes (${CLES_VALIDATION.VOIR})`
        : `la permission de lecture des saisies des enseignants, dans le module Notes (${CLES_VALIDATION.VOIR} pour le collège et le lycée, ${CLES_VALIDATION_PRIMAIRE.VOIR} pour le primaire)`);
    return (
      <div className="flex h-64 items-center justify-center px-6">
        <div className="max-w-lg rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-red-800">Accès refusé</h2>
          <p className="text-sm text-red-700">
            Les notes saisies par les enseignants se lisent avec {cles}. Rapprochez-vous de la personne qui règle les
            droits de l'établissement.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default GardeValidationNotes;
